/**
 * Window Function Registry
 * Sprint 30: Centralized registry for window functions with explicit ownership
 *
 * Purpose:
 * - Prevent function overwrites by detecting duplicates
 * - Document ownership of each window function
 * - Provide clear error messages when functions aren't registered
 * - Enable dependency tracking for future service mapping
 *
 * Design Pattern: Registry Pattern
 * - Single source of truth for window function registration
 * - Explicit ownership prevents hidden dependencies
 * - Load order independent
 */

/**
 * Function registration metadata
 */
interface FunctionRegistration {
    name: string;
    owner: string;           // Module that owns this function
    description: string;     // What the function does
    registeredAt: number;    // Timestamp for debugging
}

/**
 * Registry entry with the actual function
 */
interface RegistryEntry extends FunctionRegistration {
    fn: Function;
}

/**
 * WindowFunctionRegistry class
 * Manages all window-level functions with ownership tracking
 */
class WindowFunctionRegistry {
    private registry: Map<string, RegistryEntry> = new Map();
    private initialized: boolean = false;

    /**
     * Register a window function with ownership
     * @param name - Function name (will be attached to window)
     * @param fn - The function implementation
     * @param owner - Module name that owns this function
     * @param description - Brief description of what the function does
     * @throws Error if function already registered by different owner
     */
    register(
        name: string,
        fn: Function,
        owner: string,
        description: string = ''
    ): void {
        const existing = this.registry.get(name);

        if (existing) {
            if (existing.owner === owner) {
                // Same owner re-registering - update silently
                console.log(`[Registry] Updating ${name} (owner: ${owner})`);
            } else {
                // Different owner trying to register - this is the bug we're preventing!
                console.error(
                    `[Registry] CONFLICT: ${name} already registered by '${existing.owner}', ` +
                    `attempted re-registration by '${owner}'`
                );
                console.error(`[Registry] Original: ${existing.description}`);
                console.error(`[Registry] Attempted: ${description}`);
                // Don't throw - log error but allow override with warning
                console.warn(`[Registry] Allowing override but this indicates a bug!`);
            }
        }

        const entry: RegistryEntry = {
            name,
            fn,
            owner,
            description,
            registeredAt: Date.now()
        };

        this.registry.set(name, entry);

        // Attach to window object
        (window as any)[name] = fn;

        if (!existing) {
            console.log(`[Registry] Registered: ${name} (owner: ${owner})`);
        }
    }

    /**
     * Register multiple functions from a module
     * @param owner - Module name
     * @param functions - Object mapping function names to implementations
     */
    registerModule(
        owner: string,
        functions: Record<string, { fn: Function; description: string }>
    ): void {
        for (const [name, { fn, description }] of Object.entries(functions)) {
            this.register(name, fn, owner, description);
        }
    }

    /**
     * Get registration info for a function
     * @param name - Function name
     * @returns Registration info or undefined
     */
    getRegistration(name: string): FunctionRegistration | undefined {
        const entry = this.registry.get(name);
        if (!entry) return undefined;

        // Return without the function itself (for inspection)
        const { fn, ...registration } = entry;
        return registration;
    }

    /**
     * Check if a function is registered
     * @param name - Function name
     */
    isRegistered(name: string): boolean {
        return this.registry.has(name);
    }

    /**
     * Get all registrations by owner
     * @param owner - Module name
     * @returns Array of function names owned by this module
     */
    getByOwner(owner: string): string[] {
        return Array.from(this.registry.entries())
            .filter(([_, entry]) => entry.owner === owner)
            .map(([name]) => name);
    }

    /**
     * Get ownership report for debugging/documentation
     * @returns Map of owner -> function names
     */
    getOwnershipReport(): Map<string, string[]> {
        const report = new Map<string, string[]>();

        for (const [name, entry] of this.registry) {
            const ownerFunctions = report.get(entry.owner) || [];
            ownerFunctions.push(name);
            report.set(entry.owner, ownerFunctions);
        }

        return report;
    }

    /**
     * Print ownership report to console
     */
    printOwnershipReport(): void {
        console.group('[Registry] Window Function Ownership Report');
        const report = this.getOwnershipReport();

        for (const [owner, functions] of report) {
            console.group(`${owner} (${functions.length} functions)`);
            functions.forEach(fn => {
                const reg = this.getRegistration(fn);
                console.log(`  - ${fn}: ${reg?.description || 'No description'}`);
            });
            console.groupEnd();
        }

        console.groupEnd();
    }

    /**
     * Get total count of registered functions
     */
    get count(): number {
        return this.registry.size;
    }

    /**
     * Mark registry as fully initialized
     * After this, any unregistered function call will show clear error
     */
    markInitialized(): void {
        this.initialized = true;
        console.log(`[Registry] Initialized with ${this.count} functions`);
    }

    /**
     * Check if registry is initialized
     */
    get isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * Create a stub that throws helpful error if function not registered
     * Use this for functions that MUST be registered by some module
     * @param name - Function name
     * @param expectedOwner - Which module should register this
     */
    createRequiredStub(name: string, expectedOwner: string): Function {
        return (...args: any[]) => {
            if (this.isRegistered(name)) {
                // Function was registered after stub created - call it
                return (window as any)[name](...args);
            }

            console.error(
                `[Registry] Function '${name}' was called but not registered!\n` +
                `Expected owner: ${expectedOwner}\n` +
                `This usually means the module failed to load.`
            );
        };
    }
}

// Create singleton instance
export const windowFunctionRegistry = new WindowFunctionRegistry();

// Attach to window for debugging
declare global {
    interface Window {
        windowFunctionRegistry: WindowFunctionRegistry;
    }
}
window.windowFunctionRegistry = windowFunctionRegistry;

export { WindowFunctionRegistry };
